import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BusyButton, CopyButton, PageHeader, Field, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { formatDateTime } from "../../lib/format";
import { useAuth } from "../../auth/store";
import { staffApi, staffKeys, type StripeCatalog } from "./api";
import { DirtyActions } from "../../components/UnsavedChanges";

export function StaffPaymentsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const owner = useAuth().me?.user.staffRole === "owner";
  const billing = useQuery({ queryKey: staffKeys.billingConfig, queryFn: staffApi.billingConfig });
  const orgs = useQuery({
    queryKey: ["staff", "sandbox-orgs"],
    queryFn: () => staffApi.orgs({}, undefined, 100),
    enabled: owner,
  });
  const [secretKey, setSecretKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [testSecretKey, setTestSecretKey] = useState("");
  const [testWebhookSecret, setTestWebhookSecret] = useState("");
  const [sandboxOrgId, setSandboxOrgId] = useState<string | null>(null);
  const savedSandbox = billing.data?.sandboxOrgId ?? "";
  const sandbox = sandboxOrgId ?? savedSandbox;
  const liveDirty = !!(secretKey || webhookSecret);
  const testDirty = !!(testSecretKey || testWebhookSecret) || sandbox !== savedSandbox;
  const testReady = billing.data?.testConfigured || (!!testSecretKey && !!testWebhookSecret);

  const saveLive = useMutation({
    mutationFn: () => staffApi.saveBillingConfig({ secretKey, webhookSecret }),
    onSuccess: (config) => {
      qc.setQueryData(staffKeys.billingConfig, config);
      setSecretKey("");
      setWebhookSecret("");
      toast.push("success", "Live Stripe credentials saved securely.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const saveTest = useMutation({
    mutationFn: () => staffApi.saveBillingConfig({ testSecretKey, testWebhookSecret, sandboxOrgId: sandbox }),
    onSuccess: (config) => {
      qc.setQueryData(staffKeys.billingConfig, config);
      setTestSecretKey("");
      setTestWebhookSecret("");
      setSandboxOrgId(null);
      toast.push("success", "Test payments saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const syncBilling = useMutation({
    mutationFn: (mode: "live" | "test") => staffApi.syncBillingCatalog(mode),
    onSuccess: async (catalog, mode) => {
      await qc.invalidateQueries({ queryKey: staffKeys.billingConfig });
      toast.push("success", `Synced ${catalog.products} ${mode} products, ${catalog.prices + catalog.meterPrices} prices, and ${catalog.meters} meters.`);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const options = orgs.data?.items ?? [];
  const sandboxMissing = savedSandbox && !options.some((o) => o.id === savedSandbox);

  return (
    <div>
      <PageHeader title="Payments" />
      <div className="card section">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>Live payments</h2>
            <p className="muted small">
              Real customers check out, renew, and receive invoices with these credentials. This is never switched off.
            </p>
          </div>
          {billing.data && (
            <span className={`badge ${billing.data.configured ? "ok" : "warn"}`}>
              {billing.data.configured ? `Configured from ${billing.data.source ?? "database"}` : "Not configured"}
            </span>
          )}
        </div>
        {billing.isPending ? (
          <Skeleton lines={4} />
        ) : billing.isError ? (
          <ErrorState error={billing.error} retry={() => void billing.refetch()} />
        ) : (
          <>
            {billing.data.liveKeyMode === "test" && (
              <div className="banner warn" role="status">
                The live slot is holding a test key. Paste a live secret key so real customers are billed.
              </div>
            )}
            <Field
              label="Live secret key"
              hint={billing.data.secretKeySet ? "A key is stored. Leave blank to keep it." : "Paste the live secret key. It starts with sk_live_ or rk_live_."}
            >
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={billing.data.secretKeySet ? "••••••••••••" : "sk_live_… or rk_live_…"}
                  autoComplete="off"
                />
              )}
            </Field>
            <Field
              label="Live webhook signing secret"
              hint={
                billing.data.webhookSecretSet
                  ? "A secret is stored. Leave blank to keep it."
                  : "Copy the whsec_ value from the live webhook endpoint."
              }
            >
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={billing.data.webhookSecretSet ? "••••••••••••" : "whsec_…"}
                  autoComplete="off"
                />
              )}
            </Field>
            <Field label="Live webhook URL" hint="Add this endpoint in the live Stripe account for checkout, subscription, and invoice events.">
              {(p) => (
                <div className="row">
                  <input {...p} className="input mono" readOnly value={billing.data.webhookUrl} />
                  <CopyButton value={billing.data.webhookUrl} />
                </div>
              )}
            </Field>
            <DirtyActions
              dirty={liveDirty}
              onDiscard={() => {
                setSecretKey("");
                setWebhookSecret("");
              }}
            >
              <BusyButton
                className="btn primary"
                busy={saveLive.isPending}
                busyLabel="Saving…"
                onClick={() => saveLive.mutate()}
                disabled={!liveDirty || ((!secretKey || !webhookSecret) && !billing.data.configured)}
              >
                Save live payments
              </BusyButton>
            </DirtyActions>
          </>
        )}
      </div>

      <div className="card section" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>Test payments</h2>
            <p className="muted small">
              One organization can check out with a test card. Every other organization stays on live payments.
            </p>
          </div>
          {billing.data && (
            <span className={`badge ${billing.data.testConfigured ? "ok" : "neutral"}`}>
              {billing.data.testConfigured ? "Test keys stored" : "No test keys"}
            </span>
          )}
        </div>
        {billing.isPending ? (
          <Skeleton lines={4} />
        ) : billing.isError ? null : owner ? (
          <>
            <Field
              label="Test secret key"
              hint={billing.data.testSecretKeySet ? "A test key is stored. Leave blank to keep it." : "Paste the test secret key. It starts with sk_test_ or rk_test_."}
            >
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="password"
                  value={testSecretKey}
                  onChange={(e) => setTestSecretKey(e.target.value)}
                  placeholder={billing.data.testSecretKeySet ? "••••••••••••" : "sk_test_… or rk_test_…"}
                  autoComplete="off"
                />
              )}
            </Field>
            <Field
              label="Test webhook signing secret"
              hint={
                billing.data.testWebhookSecretSet
                  ? "A secret is stored. Leave blank to keep it."
                  : "Copy the whsec_ value from the test webhook endpoint."
              }
            >
              {(p) => (
                <input
                  {...p}
                  className="input"
                  type="password"
                  value={testWebhookSecret}
                  onChange={(e) => setTestWebhookSecret(e.target.value)}
                  placeholder={billing.data.testWebhookSecretSet ? "••••••••••••" : "whsec_…"}
                  autoComplete="off"
                />
              )}
            </Field>
            <Field label="Test webhook URL" hint="Add this endpoint in Stripe’s test mode. It rejects live events.">
              {(p) => (
                <div className="row">
                  <input {...p} className="input mono" readOnly value={billing.data.testWebhookUrl} />
                  <CopyButton value={billing.data.testWebhookUrl} />
                </div>
              )}
            </Field>
            <Field label="Sandbox organization" hint="Only this organization is charged with the test card. Leave it as none until the test keys are saved.">
              {(p) => (
                <select
                  {...p}
                  className="input"
                  value={sandbox}
                  disabled={!testReady}
                  onChange={(e) => setSandboxOrgId(e.target.value)}
                >
                  <option value="">None</option>
                  {sandboxMissing && (
                    <option value={savedSandbox}>{billing.data.sandboxOrgName || savedSandbox}</option>
                  )}
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              )}
            </Field>
            <DirtyActions
              dirty={testDirty}
              onDiscard={() => {
                setTestSecretKey("");
                setTestWebhookSecret("");
                setSandboxOrgId(null);
              }}
            >
              <BusyButton
                className="btn primary"
                busy={saveTest.isPending}
                busyLabel="Saving…"
                onClick={() => saveTest.mutate()}
                disabled={!testDirty || !testReady}
              >
                Save test payments
              </BusyButton>
            </DirtyActions>
          </>
        ) : (
          <>
            <p className="muted small">
              {billing.data.testConfigured
                ? `Test keys are stored${billing.data.sandboxOrgName ? ` for ${billing.data.sandboxOrgName}` : ""}. Only the platform owner can change test payments.`
                : "Test payments are not set up. Only the platform owner can change them."}
            </p>
            <Field label="Test webhook URL" hint="Add this endpoint in Stripe’s test mode. It rejects live events.">
              {(p) => (
                <div className="row">
                  <input {...p} className="input mono" readOnly value={billing.data.testWebhookUrl} />
                  <CopyButton value={billing.data.testWebhookUrl} />
                </div>
              )}
            </Field>
          </>
        )}
      </div>

      <CatalogBlock
        title="Live catalog"
        body="Checkout charges Stripe prices, not our own numbers. Syncing creates or updates a product and a monthly and yearly price for each paid plan, plus the usage meters. Run it after changing plan pricing or when connecting the live Stripe account."
        catalog={billing.data?.catalog}
        pending={billing.isPending}
        disabled={!billing.data?.configured || syncBilling.isPending}
        busy={syncBilling.isPending && syncBilling.variables === "live"}
        onSync={() => syncBilling.mutate("live")}
        note={billing.data && !billing.data.configured ? "Save live payments first." : undefined}
      />
      {owner && (
        <CatalogBlock
          title="Test catalog"
          body="The sandbox organization uses these prices. They are separate from the live prices because test and live accounts do not share them."
          catalog={billing.data?.testCatalog}
          pending={billing.isPending}
          disabled={!billing.data?.testConfigured || syncBilling.isPending}
          busy={syncBilling.isPending && syncBilling.variables === "test"}
          onSync={() => syncBilling.mutate("test")}
          note={billing.data && !billing.data.testConfigured ? "Save test payments first." : undefined}
        />
      )}
    </div>
  );
}

function CatalogBlock({
  title, body, catalog, pending, disabled, busy, onSync, note,
}: {
  title: string;
  body: string;
  catalog?: StripeCatalog;
  pending: boolean;
  disabled: boolean;
  busy: boolean;
  onSync: () => void;
  note?: string;
}) {
  const synced = (catalog?.products ?? 0) > 0;
  return (
    <div className="card section" style={{ marginTop: 16 }}>
      <h2>{title}</h2>
      <p className="muted small">{body}</p>
      {pending ? (
        <Skeleton lines={3} />
      ) : (
        <>
          {synced ? (
            <dl className="kv-inline small">
              <dt>Products</dt>
              <dd>{catalog?.products ?? 0}</dd>
              <dt>Plan prices</dt>
              <dd>{catalog?.prices ?? 0}</dd>
              <dt>Usage meters</dt>
              <dd>{catalog?.meters ?? 0}</dd>
              <dt>Metered prices</dt>
              <dd>{catalog?.meterPrices ?? 0}</dd>
              <dt>Last synced</dt>
              <dd>{catalog?.lastSyncedAt ? formatDateTime(catalog.lastSyncedAt) : "Never"}</dd>
            </dl>
          ) : (
            <p className="muted small">Nothing synced yet. Until this runs, checkout has no price to charge and fails.</p>
          )}
          <div className="row" style={{ marginTop: 12 }}>
            <BusyButton busy={busy} busyLabel="Syncing…" onClick={onSync} disabled={disabled}>
              {synced ? "Re-sync plans and meters" : "Sync plans and meters"}
            </BusyButton>
            {note && <span className="muted small">{note}</span>}
          </div>
        </>
      )}
    </div>
  );
}
